import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { normalize } from 'normalizr';
import { SpaceTab as SpaceTabApi } from 'api/space/space-tab';
import { itemsFetchStatus, saveNormalizedData } from 'data/core/utils';
import { SpaceTab } from 'data/space-tabs/schema';
import { EntityState, FetchStatus, SavePayloadAction } from '../core';
import { spaceTabSchema } from '../spaces/schema';
import { CreateSpaceTabPayload, UpdateSpaceTabTitle } from './actions';

const initialState: SpaceTabState = {
  items: {},
  itemsFetchStatus: {},
  loading: false,
  error: null,
  creating: false,
  createError: null,
  embedding: false,
  activeTabKey: '',
  updating: false,
  deleting: false,
  deleteError: null,
  isLoading: false,
  isError: false,
};

interface SpaceTabState extends EntityState<SpaceTab> {
  loading: boolean;
  error: null | string;
  creating: boolean;
  embedding: boolean;
  createError: null | string;
  activeTabKey: string;
  updating: boolean;
  deleting: boolean;
  deleteError: null | string;
}

export const spaceTabsSlice = createSlice({
  initialState,
  name: 'spaceTabs',

  reducers: (create) => ({
    setActiveTabKey: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<string>,
      ): SpaceTabState => {
        return {
          ...state,
          activeTabKey: payload,
        };
      },
    ),

    addSpaceTab: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<CreateSpaceTabPayload>,
      ): SpaceTabState => {
        const data = normalize(payload, spaceTabSchema);

        return merge(
          {},
          {
            ...state,
            items: { ...state.items, ...data.entities.spaceTabs },
            embedding: false,
            activeTabKey: String(payload.spaceTabId),
          },
        );
      },
    ),

    updateSpaceTabTitle: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<UpdateSpaceTabTitle>,
      ): void => {
        // As this is how immer works;

        state.items[payload.spaceId].spaceTabTitle =
          payload.spaceTabTitle ||
          state.items[payload.spaceId]?.spaceTabTitle ||
          '';
      },
    ),

    createStart: create.reducer((state: SpaceTabState) => ({
      ...state,
      creating: true,
    })),

    embedStart: create.reducer(
      (state: SpaceTabState): SpaceTabState => ({
        ...state,
        embedding: true,
      }),
    ),

    createSuccess: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<SpaceTabApi>,
      ): SpaceTabState => {
        const normalized = normalize(payload, spaceTabSchema);
        const tempState = { ...state };

        tempState.items = omit(state.items, 0);

        return merge({}, tempState, {
          items: { ...normalized.entities.spaceTabs },
          activeTabKey: String(payload.spaceTabId),
          embedding: false,
          creating: false,
          error: null,
          isError: false,
        });
      },
    ),

    createFailure: create.reducer((state: SpaceTabState): SpaceTabState => {
      return {
        ...state,
        embedding: false,
        creating: false,
        error: null,
        isError: true,
      };
    }),

    saveSpaceTabs: create.reducer(
      (
        state: SpaceTabState,
        { payload }: SavePayloadAction<SpaceTabApi[]>,
      ): SpaceTabState => {
        const { data, fetchStatus } = payload;
        const {
          entities: { spaceTabs },
          result,
        } = normalize(data, [spaceTabSchema]);

        return merge({}, state, {
          items: spaceTabs,
          itemsFetchStatus: itemsFetchStatus(
            result,
            fetchStatus,
            state.itemsFetchStatus,
          ),
          updating: false,
          creating: false,
        });
      },
    ),

    saveNormalizedSpaceTabs: create.reducer(saveNormalizedData),

    deleteStart: create.reducer((state: SpaceTabState): SpaceTabState => {
      return {
        ...state,
        deleting: true,
      };
    }),

    deleteSuccess: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<number>,
      ): SpaceTabState => {
        return {
          ...state,
          items: omit(state.items, payload),
          deleting: false,
        };
      },
    ),

    deleteFailure: create.reducer((state: SpaceTabState): SpaceTabState => {
      return {
        ...state,
        deleting: false,
      };
    }),

    updateStart: create.reducer((state: SpaceTabState) => {
      return { ...state, updating: true };
    }),

    updateSuccess: create.reducer((state: SpaceTabState) => {
      return { ...state, updating: false };
    }),

    fetchDetailStart: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<number>,
      ): SpaceTabState => {
        return {
          ...state,
          itemsFetchStatus: {
            ...state.itemsFetchStatus,
            [payload]: FetchStatus.Loading,
          },
        };
      },
    ),

    addTaskToSpaceTab: create.reducer(
      (
        state: SpaceTabState,
        { payload }: PayloadAction<{ spaceTabId: number; taskId: number }>,
      ): SpaceTabState => {
        const { spaceTabId, taskId } = payload;
        let spaceTab = state.items[spaceTabId];

        if (spaceTab) {
          spaceTab = {
            ...spaceTab,
            taskList: [...spaceTab.taskList, taskId],
          };

          return dotPropImmutable.set(state, `items.${spaceTabId}`, spaceTab);
        }

        return state;
      },
    ),

    updateSpaceTabTaskList: create.reducer(
      (
        state: SpaceTabState,
        {
          payload,
        }: PayloadAction<{
          spaceTabId: number;
          tempTaskId: number;
          taskId: number;
        }>,
      ): SpaceTabState => {
        const { spaceTabId, taskId, tempTaskId } = payload;
        let spaceTab = state.items[spaceTabId];

        if (spaceTab) {
          spaceTab = {
            ...spaceTab,
            taskList: [
              ...spaceTab.taskList.filter((t) => t !== tempTaskId),
              taskId,
            ],
          };

          return dotPropImmutable.set(state, `items.${spaceTabId}`, spaceTab);
        }

        return state;
      },
    ),

    removeTaskFromSpaceTab: create.reducer(
      (
        state,
        { payload }: PayloadAction<{ spaceTabId: number; taskId: number }>,
      ) => {
        const { spaceTabId, taskId } = payload;
        let spaceTab = state.items[spaceTabId];

        if (spaceTab) {
          spaceTab = {
            ...spaceTab,
            taskList: spaceTab.taskList.filter((t) => t !== taskId),
          };

          return dotPropImmutable.set(state, `items.${spaceTabId}`, spaceTab);
        }

        return state;
      },
    ),
  }),
});
